import { DSButton } from "@components/design-system/molecules";
import { faBone, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useAccount,
  useAuth,
  usePreferences,
  useTeam,
  useWindowDimensions,
  useRespondToStripeCheckoutComplete,
} from "@hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSpring, animated, config } from "react-spring";
import { useIntercom } from "react-use-intercom";

import AccountStatus from "./header/AccountStatus.jsx";
import LogoIcon from "../../assets/imgs/Logo_icon.png";
import { useCurrentUser } from "../../hooks/use-current-user";
import { UpgradePlanButton } from "../account/UpgradePlanButton";
import GlobalSearchInput from "../common/search/GlobalSearchInput";
import AuthNav from "../layout/header/AuthNav";
import Logo from "../layout/header/Logo";

const Header = () => {
  const [isNavbarShrunkRaw, setIsNavbarShrunk] = useState(false);
  const [headerWidth, setHeaderWidth] = useState(0);
  const headerContentRef = useRef(null);
  const auth = useAuth();
  const { width, height } = useWindowDimensions();
  const { show, update } = useIntercom();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTeam } = useTeam();
  const { isSubscribed } = useAccount();
  const { firstTimeTourDisabled } = usePreferences();
  // Keep header collapsed while the FTUE tour is running so Joyride
  // spotlight positions stay aligned with header elements.
  const isNavbarShrunk =
    firstTimeTourDisabled === false ? true : isNavbarShrunkRaw;
  const { globalSearch, releaseWebPayItFurwardReferrals } =
    useFlags();
  const { data: userData, user } = useCurrentUser();

  const referralCode = user?.referral?.referralCode;

  useEffect(() => {
    const updateHeaderWidth = () => {
      if (headerContentRef.current) {
        setHeaderWidth(headerContentRef.current.offsetWidth);
      }
    };

    updateHeaderWidth();

    const resizeObserver = new ResizeObserver(updateHeaderWidth);

    if (headerContentRef.current) {
      resizeObserver.observe(headerContentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (auth?.isUserAuthenticated && userData?.user) {
      update({
        email: userData?.user?.email,
        userId: userData?.user?.uuid,
      });
    }
  }, [auth?.isUserAuthenticated, userData?.user, update]);

  useEffect(() => {
    const handler = () => {
      setIsNavbarShrunk((isNavbarShrunk) => {
        if (
          !isNavbarShrunk &&
          (document.body.scrollTop > 25 ||
            document.documentElement.scrollTop > 25)
        ) {
          return true;
        }

        if (
          isNavbarShrunk &&
          document.body.scrollTop < 5 &&
          document.documentElement.scrollTop < 5
        ) {
          return false;
        }

        return isNavbarShrunk;
      });
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useRespondToStripeCheckoutComplete();

  // Breakpoints to hide header elements
  // This is with respect to the header width, not the screen width
  // because of the companion sidebar compressing the header
  const shouldShowTeamDropdown = !globalSearch || headerWidth > 1000;
  const shouldShowInviteTeam = !globalSearch || headerWidth > 1000;
  const shouldShowAccountStatus = !globalSearch || headerWidth > 1000;
  const shouldShowGlobalSearch = !globalSearch || headerWidth > 600;

  const headerAnim = useSpring({
    config: config.stiff,
    paddingTop: isNavbarShrunk ? "1px" : "10px",
    paddingBottom: isNavbarShrunk ? "1px" : "10px",
    // borderBottom: isNavbarShrunk
    //   ? `1px solid ${darkModeEnabled[0] ? "#3F3F46" : "#F7F7F7"}`
    //   : "1px solid transparent",
  });

  const logoShrinkAnim = useSpring({
    config: config.slow,
    width: isNavbarShrunk ? "130px" : "150px",
  });

  return (
    <animated.div
      id="header"
      style={headerAnim}
      className={`sticky top-0 z-50 w-full mx-auto px-4 md:px-8 border-b border-transparent ${
        isNavbarShrunk
          ? "bg-primary/90 backdrop-filter backdrop-blur-xs"
          : "bg-primary backdrop-filter backdrop-blur-none"
      }`}
    >
      <div
        ref={headerContentRef}
        className="flex items-center justify-between h-16"
      >
        <div className="flex items-center space-x-4 flex-1">
          <animated.div
            id="logo"
            style={logoShrinkAnim}
            className="shrink-0 cursor-pointer"
          >
            {width > 500 ? (
              <Logo />
            ) : (
              <Link to="/dashboard">
                <img width={40} src={LogoIcon} />
              </Link>
            )}
          </animated.div>

          {shouldShowAccountStatus && (
            <div className="flex items-center">
              <AccountStatus small />
            </div>
          )}
        </div>

        {auth?.isUserAuthenticated &&
          globalSearch &&
          shouldShowGlobalSearch && (
            <div className="flex-1 flex justify-center px-4 min-w-0">
              <div className="w-full max-w-[400px]">
                <GlobalSearchInput />
              </div>
            </div>
          )}

        <div className="flex items-center space-x-2 flex-1 justify-end min-w-0">
          {auth?.isUserAuthenticated &&
          user &&
          !currentTeam?.uuid &&
          shouldShowInviteTeam ? (
            <button
              className="text-brand mr-3"
              onClick={() => {
                navigate("/teams");
              }}
              data-testid="invite-my-team-button"
            >
              Invite My Team <FontAwesomeIcon icon={faUserPlus} />
            </button>
          ) : null}

          {auth?.isUserAuthenticated &&
          user &&
          releaseWebPayItFurwardReferrals &&
          isSubscribed &&
          referralCode &&
          shouldShowInviteTeam ? (
            <button
              className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors mx-md"
              onClick={() => {
                mixpanel.track("Referral Link Clicked - Header", {
                  referralCode,
                });
                navigate("/account");
              }}
              data-testid="refer-a-friend-button"
              title="Pay It Furward"
            >
              Pay It Furward <FontAwesomeIcon icon={faBone} />
            </button>
          ) : null}

          {!globalSearch &&
            user &&
            !isSubscribed &&
            auth?.isUserAuthenticated &&
            width > 600 &&
            height > 600 && (
              <UpgradePlanButton featureName="Header" />
            )}
          {auth?.isUserAuthenticated && user ? (
            <AuthNav
              show={show}
              shouldShowTeamDropdown={shouldShowTeamDropdown}
            />
          ) : !auth?.isUserAuthenticated ? (
            location.pathname !== "/" &&
            location.pathname !== "/auth/login" &&
            !location.pathname.startsWith("/templates/shared/") && (
              <DSButton
                variant="primary"
                onClick={() => {
                  navigate(
                    `/auth/login?next=${encodeURIComponent(location.pathname + location.search)}`,
                  );
                }}
                dataTestId="header-sign-in-button"
                data-mx-label="header-sign-in-button"
              >
                Sign In
              </DSButton>
            )
          ) : null}
        </div>
      </div>
    </animated.div>
  );
};

export default Header;
